import { useEffect, useState } from 'react';
import { Save, Plus, Trash2, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  getQuizSettings, 
  updateQuizSettings, 
  getQuizQuestions, 
  createQuizQuestion, 
  updateQuizQuestion, 
  deleteQuizQuestion 
} from '@/lib/api';

export default function AdminQuiz() {
  const [settings, setSettings] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, questionsRes] = await Promise.all([
        getQuizSettings(),
        getQuizQuestions()
      ]);
      setSettings(settingsRes.data);
      setQuestions(questionsRes.data);
    } catch (err) {
      toast.error('Не удалось загрузить данные');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateQuizSettings(settings);
      toast.success('Настройки сохранены');
    } catch (err) {
      toast.error('Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const handleAddQuestion = async () => {
    try {
      const newQuestion = {
        question: 'Новый вопрос',
        options: [
          { text: 'Вариант 1', emoji: '' },
          { text: 'Вариант 2', emoji: '' }
        ],
        order: questions.length,
        is_active: true
      };
      const res = await createQuizQuestion(newQuestion);
      setQuestions([...questions, res.data]);
      toast.success('Вопрос добавлен');
    } catch (err) {
      toast.error('Не удалось добавить вопрос');
    }
  };

  const handleUpdateQuestion = async (question) => {
    try {
      await updateQuizQuestion(question.id, {
        question: question.question,
        options: question.options.map(o => ({ text: o.text, emoji: o.emoji || '' })),
        order: question.order,
        is_active: question.is_active
      });
      toast.success('Вопрос обновлен');
    } catch (err) {
      toast.error('Не удалось обновить вопрос');
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm('Удалить этот вопрос?')) return;
    try {
      await deleteQuizQuestion(id);
      setQuestions(questions.filter(q => q.id !== id));
      toast.success('Вопрос удален');
    } catch (err) {
      toast.error('Не удалось удалить вопрос');
    }
  };

  const updateQuestionField = (index, field, value) => {
    const newQuestions = [...questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setQuestions(newQuestions);
  };

  const addOption = (questionIndex) => {
    const newQuestions = [...questions];
    newQuestions[questionIndex].options.push({ text: '', emoji: '' });
    setQuestions(newQuestions);
  };

  const removeOption = (questionIndex, optionIndex) => {
    const newQuestions = [...questions];
    newQuestions[questionIndex].options = newQuestions[questionIndex].options.filter((_, i) => i !== optionIndex);
    setQuestions(newQuestions);
  };

  const updateOption = (questionIndex, optionIndex, field, value) => {
    const newQuestions = [...questions];
    newQuestions[questionIndex].options[optionIndex] = {
      ...newQuestions[questionIndex].options[optionIndex],
      [field]: value
    };
    setQuestions(newQuestions);
  };

  const moveQuestion = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= questions.length) return;
    
    const newQuestions = [...questions];
    const temp = newQuestions[index];
    newQuestions[index] = newQuestions[newIndex];
    newQuestions[newIndex] = temp;
    
    // Update order values
    newQuestions.forEach((q, i) => { q.order = i; });
    setQuestions(newQuestions);
    
    // Save both questions
    try {
      await Promise.all([
        updateQuizQuestion(newQuestions[index].id, { ...newQuestions[index], options: newQuestions[index].options.map(o => ({ text: o.text, emoji: o.emoji || '' })) }),
        updateQuizQuestion(newQuestions[newIndex].id, { ...newQuestions[newIndex], options: newQuestions[newIndex].options.map(o => ({ text: o.text, emoji: o.emoji || '' })) })
      ]);
    } catch (err) {
      toast.error('Не удалось изменить порядок');
    }
  };

  if (busy) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-quiz">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Квиз</h1>
          <p className="text-slate-500">Настройка вопросов и текстов квиза</p>
        </div>
      </div>

      {/* Quiz Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Настройки квиза</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Включить квиз</Label>
              <p className="text-sm text-slate-500">Показывать квиз перед менеджером</p>
            </div>
            <Switch
              checked={settings?.is_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, is_enabled: checked })}
            />
          </div>

          <div className="space-y-2">
            <Label>Заголовок Hero-блока</Label>
            <Input
              value={settings?.hero_title || ''}
              onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
              placeholder="Официальная работа в Великобритании..."
            />
          </div>

          <div className="space-y-2">
            <Label>Подзаголовок Hero-блока</Label>
            <Textarea
              value={settings?.hero_subtitle || ''}
              onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
              placeholder="Пройдите короткий тест..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Текст кнопки</Label>
            <Input
              value={settings?.hero_button || ''}
              onChange={(e) => setSettings({ ...settings, hero_button: e.target.value })}
              placeholder="Пройти проверку"
            />
          </div>

          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium mb-3">Страница результата</h4>
            
            <div className="space-y-2">
              <Label>Заголовок результата</Label>
              <Input
                value={settings?.result_title || ''}
                onChange={(e) => setSettings({ ...settings, result_title: e.target.value })}
                placeholder="Поздравляем! Вы подходите..."
              />
            </div>

            <div className="space-y-2 mt-3">
              <Label>Текст результата</Label>
              <Textarea
                value={settings?.result_text || ''}
                onChange={(e) => setSettings({ ...settings, result_text: e.target.value })}
                placeholder="На основании ваших ответов..."
                rows={3}
              />
            </div>

            <div className="space-y-2 mt-3">
              <Label>Блок доверия (по одному пункту на строку)</Label>
              <Textarea
                value={settings?.result_trust_points?.join('\n') || ''}
                onChange={(e) => setSettings({ ...settings, result_trust_points: e.target.value.split('\n').filter(s => s.trim()) })}
                placeholder="Работаем официально&#10;Контракт напрямую..."
                rows={4}
              />
            </div>
          </div>

          <Button onClick={handleSaveSettings} disabled={saving} className="mt-4">
            {saving ? 'Сохранение...' : <><Save className="w-4 h-4 mr-2" /> Сохранить настройки</>}
          </Button>
        </CardContent>
      </Card>

      {/* Questions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Вопросы ({questions.length})</CardTitle>
          <Button onClick={handleAddQuestion} size="sm">
            <Plus className="w-4 h-4 mr-2" /> Добавить вопрос
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {questions.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Нет вопросов. Добавьте первый вопрос.</p>
          ) : (
            questions.map((q, qIndex) => (
              <div key={q.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="flex flex-col gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => moveQuestion(qIndex, 'up')}
                      disabled={qIndex === 0}
                      className="h-6 w-6 p-0"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => moveQuestion(qIndex, 'down')}
                      disabled={qIndex === questions.length - 1}
                      className="h-6 w-6 p-0"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-500">#{qIndex + 1}</span>
                      <Input
                        value={q.question}
                        onChange={(e) => updateQuestionField(qIndex, 'question', e.target.value)}
                        onBlur={() => handleUpdateQuestion(q)}
                        placeholder="Текст вопроса"
                        className="flex-1"
                      />
                      <Switch
                        checked={q.is_active}
                        onCheckedChange={(checked) => {
                          updateQuestionField(qIndex, 'is_active', checked);
                          handleUpdateQuestion({ ...q, is_active: checked });
                        }}
                      />
                    </div>

                    <div className="space-y-2 pl-4">
                      <Label className="text-xs text-slate-500">Варианты ответов</Label>
                      {q.options?.map((opt, oIndex) => (
                        <div key={opt.id || oIndex} className="flex items-center gap-2">
                          <Input
                            value={opt.emoji || ''}
                            onChange={(e) => updateOption(qIndex, oIndex, 'emoji', e.target.value)}
                            onBlur={() => handleUpdateQuestion(q)}
                            placeholder="🇺🇿"
                            className="w-16"
                          />
                          <Input
                            value={opt.text}
                            onChange={(e) => updateOption(qIndex, oIndex, 'text', e.target.value)}
                            onBlur={() => handleUpdateQuestion(q)}
                            placeholder="Текст варианта"
                            className="flex-1"
                          />
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              removeOption(qIndex, oIndex);
                              handleUpdateQuestion({ ...q, options: q.options.filter((_, i) => i !== oIndex) });
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => addOption(qIndex)}
                        className="mt-2"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Вариант
                      </Button>
                    </div>
                  </div>

                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleDeleteQuestion(q.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
